/**
 * The round currently being played, and who it is against.
 *
 * A deliberately small piece of state sitting between the game and the API. It
 * exists so `main` never has to reason about network failure in the middle of a
 * round: it asks for a match, gets one (possibly a solo one), plays it, and
 * hands back a score. Whether any of that reached a server is this module's
 * problem, not the game's.
 */

import { type FinalScore } from '@/game/types';
import type { ChallengeView, StakedDeal } from '@/net/api';
import {
  createLobby,
  joinLobby,
  nextAsyncChallenge,
    type Opponent,
  type Verdict,
} from '@/net/api';
import { lobbyLink, playerName } from '@/net/session';

export type MatchKind = 'solo' | 'async' | 'lobby' | 'staked';

export interface Match {
  readonly kind: MatchKind;
  /** Null when this round is not being recorded anywhere. */
  readonly challengeId: string | null;
  /** The score to beat, if somebody has already played this deck. */
  readonly target: Opponent | null;
  /** Set for a lobby: the code, and the link to send. */
  readonly lobby: { code: string; link: string; hostName: string } | null;
  /** Why this is a solo game when the player did not ask for one. */
  readonly degradedReason: string | null;
  /** Which seat this player holds on a staked challenge; the results tray's second button reads it. */
  readonly role?: 'creator' | 'taker';
  /**
   * Set for a staked challenge: what it cost and what it pays.
   *
   * Carried on the match rather than looked up when needed, because the round
   * is played against a pot that was fixed the moment the stake was taken. A
   * fee re-read at the end could differ from the one the player agreed to.
   */
  readonly stake: { entryFee: number; pot: number; payout: number } | null;
}

export const SOLO: Match = {
  kind: 'solo',
  challengeId: null,
  target: null,
  lobby: null,
  degradedReason: null,
  stake: null,
};

/** A solo round, but say why — the player asked for a challenge and got this. */
function degraded(reason: string): Match {
  return { ...SOLO, degradedReason: reason };
}

/** Take the next deck out of the shared pool. */
export async function takeAsyncMatch(): Promise<Match> {
  const response = await nextAsyncChallenge();
  if (!response.ok) return degraded(response.error);

  const { challengeId, target } = response.value;
  return { kind: 'async', challengeId, target, lobby: null, degradedReason: null, stake: null };
}

/** Open a lobby and get a link to send. */
export async function openLobbyMatch(href: string): Promise<Match> {
  const name = playerName();
  const response = await createLobby(name);
  if (!response.ok) return degraded(response.error);

  const { code, challengeId } = response.value;
  return {
    kind: 'lobby',
    challengeId,
    target: null,
    lobby: { code, link: lobbyLink(code, href), hostName: name },
    degradedReason: null,
    stake: null,
  };
}

/** Join a lobby somebody sent. */
export async function joinLobbyMatch(code: string, href: string): Promise<Match> {
  const response = await joinLobby(code);
  if (!response.ok) return degraded(response.error);

  const lobby = response.value;
  // A staked challenge is taken by its code (and charged), never opened from a link: its id is not in this reply.
  if (lobby.challengeId === null) return degraded('that is a staked challenge: take it with its code');
  // Whoever else is on the board is the person to beat. On an empty lobby this
  // is null and the joiner is simply first, which happens when the host sends
  // the link before playing.
  const target = lobby.results.find((r) => r.name !== '' && r.score >= 0) ?? null;

  return {
    kind: 'lobby',
    challengeId: lobby.challengeId,
    target: target && lobby.results.length > 0 ? (target as Opponent) : null,
    lobby: {
      code: lobby.code,
      link: lobbyLink(lobby.code, href),
      hostName: lobby.hostName,
    },
    degradedReason: null,
    stake: null,
  };
}

export interface MatchResult {
  readonly outcome: Verdict | 'offline';
  readonly opponent: Opponent | null;
  readonly score: number;
  /** False when this player had already posted a score for this deck. */
  readonly submitted: boolean;
}



/**
 * A match on a staked challenge, from the deal the server just handed back.
 *
 * No network call of its own: the stake has already been taken by whichever
 * call produced the deal, and re-deriving a match from a second request would
 * open a window where the money has moved and the round has not.
 */
export function stakedMatch(deal: StakedDeal, role: 'creator' | 'taker'): Match {
  return {
    kind: 'staked',
    role,
    challengeId: deal.challengeId,
    // The opponent is unknown at this point by design: on a staked deck neither
    // player sees the other's score until they have posted one of their own.
    target: null,
    lobby: deal.code ? { code: deal.code, link: '', hostName: '' } : null,
    degradedReason: null,
    stake: { entryFee: deal.entryFee, pot: deal.pot, payout: deal.payout },
  };
}

/**
 * A match on a challenge this player holds a seat in but has not played —
 * the one reached from a history row or a notification, where no deal was
 * just handed back.
 *
 * `openChallenge()` used to show the page and leave `match` alone, so "Play
 * your game" dealt whatever the last round was: after a page load, solo. The
 * stake had been taken; the round was played unranked; the results plate
 * showed a solo score and the challenge stayed on YOUR TURN. The pot is
 * `entryFee × 2` and the payout the pot, which is what every deal on this
 * ladder has said while the rake is off.
 */
export function matchFromView(view: ChallengeView): Match {
  return {
    kind: 'staked',
    role: view.role,
    challengeId: view.id,
    target: null,
    lobby: view.code ? { code: view.code, link: '', hostName: '' } : null,
    degradedReason: null,
    stake: { entryFee: view.entryFee, pot: view.entryFee * 2, payout: view.entryFee * 2 },
  };
}
